import { toast } from 'sonner';

import { ApiError, TunnelResponse } from '@/hooks/useAegisManagement';
import { Agent } from '@/hooks/useAgents';
import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import type { UseTunnelManagementReturn } from '@/sections/agents/types/hooks.types';
import { useMutation } from '@/utils/api';

/**
 * Domain-specific hook for managing Cloudflare tunnel operations
 *
 * Provides focused functionality for tunnel provisioning and removal,
 * separating concerns from the main AegisManagement hook.
 *
 * @param enabled - Whether the hook should be active (useful for modal state)
 */
export const useTunnelManagement = (
  enabled = true
): UseTunnelManagementReturn => {
  const axios = useAxios();

  // Create tunnel mutation
  const createTunnelMutation = useMutation<TunnelResponse, ApiError, string>({
    defaultErrorMessage: 'Failed to create tunnel',
    mutationFn: async (aegisClientId: string): Promise<TunnelResponse> => {
      const promise = axios.post('/aegis/management/cloudflare/tunnel/create', {
        aegisClientId,
      });

      toast.promise(promise, {
        loading: 'Creating Cloudflare tunnel...',
        success: response => {
          const data = response.data as TunnelResponse;
          if (data.hostname) {
            return `Cloudflare tunnel created successfully! Hostname: ${data.hostname}${
              data.installTaskId
                ? ` Installation task: ${data.installTaskId}`
                : ''
            }`;
          }
          return data.message || 'Cloudflare tunnel created successfully!';
        },
        error: (err: ApiError) => {
          switch (err.type) {
            case 'network':
              return (
                err.message || 'Network error occurred while creating tunnel'
              );
            case 'validation':
              return (
                err.response?.data?.message ||
                'Invalid data provided for tunnel creation'
              );
            case 'server':
              return (
                err.response?.data?.message ||
                'Server error occurred while creating tunnel'
              );
            default:
              return (
                err.response?.data?.message ||
                err.message ||
                'Failed to create tunnel'
              );
          }
        },
      });

      const response = await promise;
      return response.data;
    },
    onSuccess: async () => {
      // Invalidate both tasks and agents - invalidation automatically triggers refetch
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getAegisManagementTasks(),
      });
    },
  });

  // Remove tunnel mutation
  const removeTunnelMutation = useMutation<
    { taskId?: string },
    ApiError,
    string
  >({
    defaultErrorMessage: 'Failed to remove tunnel',
    mutationFn: async (aegisClientId: string) => {
      const promise = axios.post('/aegis/management/cloudflare/tunnel/remove', {
        aegisClientId,
      });

      toast.promise(promise, {
        loading: 'Removing Cloudflare tunnel...',
        success: response => {
          const data = response.data as { taskId?: string };
          if (data?.taskId) {
            return `Tunnel removal task created: ${data.taskId}`;
          }
          return 'Tunnel removed successfully';
        },
        error: (err: ApiError) => {
          switch (err.type) {
            case 'network':
              return (
                err.message || 'Network error occurred while removing tunnel'
              );
            case 'validation':
              return (
                err.response?.data?.message ||
                'Invalid data provided for tunnel removal'
              );
            case 'server':
              return (
                err.response?.data?.message ||
                'Server error occurred while removing tunnel'
              );
            default:
              return (
                err.response?.data?.message ||
                err.message ||
                'Failed to remove tunnel'
              );
          }
        },
      });

      const response = await promise;
      return response.data;
    },
    onSuccess: async () => {
      // Invalidate both tasks and agents - invalidation automatically triggers refetch
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getAegisManagementTasks(),
      });
    },
  });

  // Helper function to get tunnel info from agent health check
  const getTunnelInfo = (agent?: Agent) => {
    if (!agent?.health_check) return { status: 'not_configured' as const };

    const { cloudflared_status } = agent.health_check;
    const isConfigured =
      cloudflared_status?.status === 'running' &&
      cloudflared_status?.connected === 'true';

    return {
      status: isConfigured
        ? ('configured' as const)
        : ('not_configured' as const),
      hostname: cloudflared_status?.hostname,
      tunnelId: cloudflared_status?.tunnel_name,
      tunnelName: cloudflared_status?.tunnel_name,
      lastHealthCheck: agent.last_seen_at
        ? new Date(agent.last_seen_at / 1000).toISOString()
        : undefined,
    };
  };

  return {
    createTunnel: createTunnelMutation.mutateAsync,
    removeTunnel: removeTunnelMutation.mutateAsync,
    getTunnelInfo,
    isCreatingTunnel: createTunnelMutation.isPending,
    isRemovingTunnel: removeTunnelMutation.isPending,
    isLoading: createTunnelMutation.isPending || removeTunnelMutation.isPending,
    enabled,
  };
};
